import { useState } from "react";
import { ChevronDown, ChevronRight, Ship } from "lucide-react";

const VesselStateTransition = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const vesselStates = [
    {
      id: "atSea",
      name: "At sea",
      description: "Vessel actively sailing in open waters",
      characteristics: [
        "Primary transit mode between ports",
        "Highest fuel consumption typically",
        "Continuous navigation operations"
      ],
      color: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "maneuvering",
      name: "Maneuvering",
      description: "Vessel in port approach/departure operations",
      characteristics: [
        "Slower speed operations near ports",
        "Increased engine maneuvering",
        "Pilot and tug assistance common"
      ],
      color: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "maneuveringAnchored",
      name: "Maneuvering (anchored)",
      description: "Vessel maneuvering while at anchor position",
      characteristics: [
        "Anchor operations with engine use",
        "Position maintenance at anchorage",
        "Waiting for port clearance"
      ],
      color: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "inPort",
      name: "In Port",
      description: "Vessel berthed at port for cargo operations",
      characteristics: [
        "Cargo loading/unloading operations",
        "Auxiliary power generation",
        "Port services and shore power"
      ],
      color: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "atSeaDrifting",
      name: "At sea (drifting)",
      description: "Vessel drifting with minimal engine use",
      characteristics: [
        "Minimal fuel consumption",
        "Waiting for instructions or weather",
        "Environmental considerations"
      ],
      color: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    }
  ];

  const analysisMetrics = [
    {
      id: "stateTransitionData",
      title: "State Transition Data Structure",
      description: "How state information is stored in VesselFuelParameter collection",
      calculation: "stateTransition = { raw: 'At sea', value: [val1] }",
      calculationDetails: [
        "Data Source: VesselFuelParameter.stateTransition field",
        "Raw Format: Pipe-separated state names (e.g., 'At sea')",
        "Value Array: Numeric values corresponding to each state",
        "Purpose: Tracks allocation of time, distance, or quantity across vessel states"
      ],
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "percentageCalculation",
      title: "Percentage Calculation",
      description: "How state percentages are calculated from summed values",
      calculation: "percentage = (stateSum / totalSum) × 100",
      calculationDetails: [
        "Total Sum: Sum of all valid values across all states and records",
        "State Sum: Sum of values for a specific state across all records",
        "Percentage: (stateSum ÷ totalSum) × 100",
        "Precision: Rounded to 2 decimal places",
        "Zero Total Handling: Returns 0% if totalSum = 0"
      ],
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fad8d4] rounded-lg">
              <Ship className="w-6 h-6 text-[#a93226]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Vessel State Transition Analysis
              </h2>
              <p className="text-gray-600 text-sm">
                Operational state percentage analysis from VesselFuelParameter data
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Vessel States Information */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">Vessel Operational States</h3>
              <p className="text-gray-600 text-sm mb-3">Common vessel states tracked in the system:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {vesselStates.map((state) => (
                  <div
                    key={state.id}
                    className={`p-3 rounded-lg border ${state.color} ${state.bgColor}`}
                  >
                    <h4 className="font-semibold text-gray-800 text-sm mb-2">{state.name}</h4>
                    <p className="text-xs text-gray-600 mb-2">{state.description}</p>
                    <ul className="text-xs text-gray-700 space-y-1">
                      {state.characteristics.map((char, index) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-1">•</span>
                          <span className="text-xs">{char}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Analysis Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5">
              {analysisMetrics.map((metric) => (
                <div
                  key={metric.id}
                  className={`p-5 rounded-lg border ${metric.borderColor} ${metric.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {metric.title}
                  </h4>
                  
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                    <p className={`text-sm ${metric.textColor}`}>
                      {metric.description}
                    </p>
                  </div>

                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                    <div className="p-2 bg-white rounded border border-gray-200">
                      <p className={`font-mono text-sm ${metric.textColor}`}>
                        {metric.calculation}
                      </p>
                    </div>
                  </div>

                  {metric.calculationDetails && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Implementation Details:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {metric.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VesselStateTransition;